import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, CheckCircle, Sparkles, Zap, Eye, Palette, Star } from "lucide-react";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import Header from "@/components/Header";

const PERKS = [
  { icon: Eye, text: "See who liked & viewed your profile" },
  { icon: Zap, text: "Unlimited likes & matches" },
  { icon: Sparkles, text: "Profile boost included monthly" },
  { icon: Palette, text: "Premium Y2K themes & glitter cursors" },
  { icon: Star, text: "Top 20 friends instead of Top 8" },
  { icon: Crown, text: "Gold verified badge on profile" },
];

export default function Premium() {
  const { user, refreshUser } = useAuth();
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState("monthly");
  const [loading, setLoading] = useState(false);
  const [premiumStatus, setPremiumStatus] = useState(null);

  const loadData = useCallback(async () => {
    const [pkgRes, statusRes] = await Promise.all([
      fetch(`${API}/premium/packages`),
      fetch(`${API}/premium/me`, { credentials: "include" }),
    ]);
    if (pkgRes.ok) setPackages((await pkgRes.json()).packages);
    if (statusRes.ok) setPremiumStatus(await statusRes.json());
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      // Only the monthly package supports recurring (quarterly/yearly are deprecated until Stripe Prices added)
      if (selected === "monthly") {
        const res = await fetch(`${API}/recurring/subscribe`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ item_id: "gold_monthly", origin_url: window.location.origin }),
        });
        if (!res.ok) throw new Error((await res.json()).detail || "Failed");
        const data = await res.json();
        window.location.href = data.url;
        return;
      }
      // Legacy one-time for quarterly/yearly
      const res = await fetch(`${API}/premium/create-session`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ package_id: selected, origin_url: window.location.origin }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Failed");
      const data = await res.json();
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "linear-gradient(180deg, #1a0033 0%, #000022 100%)" }}>
      <Header />
      <main className="flex-1 container mx-auto px-4 py-8 max-w-3xl">
        <div className="text-center mb-8">
          <Crown className="w-16 h-16 mx-auto text-[#FFD700] mb-3" style={{ filter: "drop-shadow(0 0 20px #FFD700)" }} />
          <h1 className="text-3xl sm:text-4xl font-bold text-[#FFD700] mb-2" style={{ textShadow: "0 0 15px #FFD700" }} data-testid="premium-page-title">
            OurSpacez Gold
          </h1>
          <p className="text-[#FF66FF]">Unlock the full Y2K experience</p>
        </div>

        {premiumStatus?.is_premium && (
          <div className="myspace-card mb-6 border-[#00FF00]" data-testid="premium-active-banner">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-[#00FF00]" />
              <div>
                <p className="text-[#00FF00] font-bold">Gold Active</p>
                <p className="text-sm text-[#99CCFF]">
                  Expires: {premiumStatus.premium_expires_at?.slice(0, 10)}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="myspace-card mb-6">
          <h2 className="text-lg font-bold text-[#00FFFF] mb-4">What you get</h2>
          <ul className="space-y-3">
            {PERKS.map((perk) => (
              <li key={perk.title || perk.label} className="flex items-center gap-3 text-[#FF66FF]">
                <perk.icon className="w-5 h-5 text-[#FFD700] flex-shrink-0" />
                <span>{perk.text}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="space-y-3 mb-6">
          {packages.map((pkg) => (
            <button
              key={pkg.id}
              onClick={() => setSelected(pkg.id)}
              className={`w-full p-4 border-2 text-left transition-all ${
                selected === pkg.id
                  ? "border-[#FFD700] bg-[rgba(255,215,0,0.1)]"
                  : "border-[#6600CC] hover:border-[#FF00FF]"
              }`}
              data-testid={`premium-package-${pkg.id}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-bold text-[#00FFFF]">{pkg.label}</p>
                  <p className="text-sm text-[#99CCFF]">${pkg.amount} • {pkg.days} days</p>
                </div>
                {selected === pkg.id && <CheckCircle className="w-6 h-6 text-[#FFD700]" />}
              </div>
            </button>
          ))}
        </div>

        <button
          onClick={handleSubscribe}
          disabled={loading || !user}
          className="myspace-btn-primary w-full text-lg py-4 disabled:opacity-50"
          data-testid="subscribe-premium-btn"
        >
          {loading ? "Redirecting to Stripe..." : `Get Gold • $${packages.find(p => p.id === selected)?.amount || ""}`}
        </button>

        <p className="text-xs text-[#99CCFF] text-center mt-4">
          Secure payment via Stripe. One-time charge — no auto-renewal.
        </p>
      </main>
    </div>
  );
}

export function PremiumSuccess() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [searchParams] = useState(new URLSearchParams(window.location.search));
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");

  const pollStatus = useCallback(async (sid, attempt) => {
    if (attempt >= 6) { setStatus("timeout"); return; }
    try {
      const res = await fetch(`${API}/premium/status/${sid}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refreshUser();
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
      }
    } catch (e) { /* retry */ }
    setTimeout(() => pollStatus(sid, attempt + 1), 2000);
  }, [refreshUser]);

  useEffect(() => {
    if (sessionId) pollStatus(sessionId, 0);
  }, [sessionId, pollStatus]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(180deg, #1a0033 0%, #000022 100%)" }}>
      <div className="myspace-card text-center max-w-md w-full">
        {status === "checking" && (
          <>
            <div className="w-12 h-12 border-4 border-[#FFD700] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-[#FFD700]">Activating Gold...</p>
          </>
        )}
        {status === "paid" && (
          <>
            <Crown className="w-20 h-20 mx-auto text-[#FFD700] mb-4" style={{ filter: "drop-shadow(0 0 20px #FFD700)" }} />
            <h2 className="text-3xl font-bold text-[#FFD700] mb-2" style={{ textShadow: "0 0 15px #FFD700" }} data-testid="premium-success-title">
              Welcome to Gold!
            </h2>
            <p className="text-[#FF66FF] mb-6">All premium features unlocked.</p>
            <button onClick={() => navigate("/dashboard")} className="myspace-btn-primary" data-testid="premium-success-continue-btn">
              Continue
            </button>
          </>
        )}
        {(status === "timeout" || status === "expired") && (
          <>
            <p className="text-[#FF6600] text-lg font-bold mb-4">Payment could not be verified</p>
            <button onClick={() => navigate("/premium")} className="myspace-btn">Try Again</button>
          </>
        )}
      </div>
    </div>
  );
}
